<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import {
  AGENCY_SELLING_PRICE,
  AIR_ONLY,
  COST_ADJUSTMENT_SUMMARY,
  COST_CAD_SUMMARY,
  COST_SUMMARY,
  CRUISE,
  FLIGHT_COST_CAD,
  FLIGHT_TAXES,
  HOTEL_COST_LOCAL,
  HOTEL_ONLY,
  NUMBER_OF_ADULT_AND_CHILD,
  SELLING_PRICE_SUMMARY,
  SELLING_TAXES,
  SUB_TOTAL_COST,
  SUB_TOTAL_SELLING_PRICE,
  TOTAL_COST,
  TOTAL_SELLING_PRICE,
  TOUR,
  TRANSFER_COST,
  BASE_COST,
  BASE_TAXES,
  NCCF
} from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import { formatDate } from '@/utils/dateUtils';

interface Props {
  dataPricing?: any;
  infoData?: any;
  quoteType?: string;
  isOnlySellingPrice?: boolean;
  isHotelCruise?: boolean;
}
const props = defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const staticColumns = [
  { field: 'singlePrice', header: t('book_form.single') },
  { field: 'doublePrice', header: t('book_form.double') },
  { field: 'triplePrice', header: t('book_form.triple') },
  { field: 'quadPrice', header: t('book_form.quad') },
  { field: 'fiveInRoomPrice', header: t('book_form.5_in_room') },
  { field: 'sixInRoomPrice', header: t('book_form.6_in_room') }
];

const staticColumnFlights = [
  { field: 'adt', header: t('worksheet.12_and_up') },
  { field: 'chd', header: t('common.child_2_11') }
];

const getColumnTable = (type: string) => {
  const key = type === SELLING_PRICE_SUMMARY ? SELLING_PRICE_SUMMARY : COST_SUMMARY;
  const priceRows = props.dataPricing?.[key] ?? [];
  const row = priceRows.numOfAdultsAndChild;
  const titleChild = type === SELLING_PRICE_SUMMARY ? priceRows?.sellingPriceSummaryTitle : priceRows?.costSummaryTitle;
  if (!row) {
    return [];
  }

  const childPrices = Array.isArray(row.childPrices) ? row.childPrices : [];
  const childColumns = childPrices
    .map((_: any, index: number) => ({
      field: 'childPrices',
      header: titleChild.childPrices[index] ?? '',
      indexColumnChild: index
    }))
    .filter((col: any, index: number) => {
      const val = Number(childPrices[index]);
      return !isNaN(val) && val > 0;
    });

  const filteredStatic = staticColumns.filter((col) => {
    const val = Number(row[col.field]);
    return !isNaN(val) && val > 0;
  });

  const filteredStaticAir = staticColumnFlights.filter((col) => {
    const val = Number(row[col.field]);
    return !isNaN(val) && val > 0;
  });

  const infantValue = Number(row.infantPrice);
  const infantColumn = !isNaN(infantValue) && infantValue > 0 ? [{ field: 'infantPrice', header: t('common.infant') }] : [];

  switch (props.quoteType) {
    case AIR_ONLY:
      return [...filteredStaticAir];
    case TOUR:
      return [...filteredStatic, ...childColumns, ...infantColumn];
    default:
      return [...filteredStatic, ...childColumns, ...infantColumn];
  }
};

const titleRows = [
  { key: AGENCY_SELLING_PRICE, name: t('book_form.booking_price_summary.selling_price') },
  { key: SELLING_TAXES, name: t('book_form.booking_price_summary.selling_taxes') },
  { key: SUB_TOTAL_SELLING_PRICE, name: t('book_form.booking_price_summary.sub_total_selling_price') },
  { key: TOTAL_SELLING_PRICE, name: t('book_form.booking_price_summary.total_selling_price') },
  { key: HOTEL_COST_LOCAL, name: t('book_form.booking_price_summary.hotel_cost_local') },
  { key: COST_CAD_SUMMARY, name: t('book_form.booking_price_summary.hotel_cost_cad') },
  { key: COST_ADJUSTMENT_SUMMARY, name: t('book_form.booking_price_summary.cost_adjustment') },
  { key: TRANSFER_COST, name: t('book_form.booking_price_summary.transfer_cost') },
  { key: FLIGHT_COST_CAD, name: t('book_form.booking_price_summary.flight_cost_cad') },
  { key: FLIGHT_TAXES, name: t('book_form.booking_price_summary.flight_taxes') },
  { key: SUB_TOTAL_COST, name: t('book_form.booking_price_summary.sub_total_cost') },
  { key: TOTAL_COST, name: t('book_form.booking_price_summary.total_cost') },
  { key: NUMBER_OF_ADULT_AND_CHILD, name: t('book_form.booking_price_summary.number_of_adult_and_child') },
  { key: SELLING_PRICE_SUMMARY, name: t('book_form.booking_price_summary.selling_price_summary') },
  { key: COST_SUMMARY, name: t('book_form.booking_price_summary.cost_summary') },
  { key: NCCF, name: t('book_form.booking_price_summary.nccf') }
];

const convertOccNameTable = (rowName: string) => {
  const fareType = props.dataPricing.fareTypeSelected;
  const isBase = rowName === BASE_COST || rowName === BASE_TAXES;

  if (isBase) {
    const keyType = rowName === BASE_COST ? 'base_cost' : 'base_taxes';
    const fareKey = ['published', 'groupsB', 'vac'].includes(fareType) ? fareType : 'default';
    return t(`book_form.booking_price_summary.${fareKey}_${keyType}`);
  }

  return titleRows.find((th: any) => th.key === rowName)?.name;
};

const formatData = (price: any) => {
  const result = [{ key: SELLING_PRICE_SUMMARY, data: price.sellingPriceSummary }];
  if (!props.isOnlySellingPrice) {
    result.push({ key: COST_SUMMARY, data: price.costSummary });
  }
  return result;
};

const filterRowTable = (type: string) => {
  const priceRows = [];
  if (type === SELLING_PRICE_SUMMARY) {
    if (props.quoteType === CRUISE) {
      priceRows.push(AGENCY_SELLING_PRICE, SELLING_TAXES, NCCF, SUB_TOTAL_SELLING_PRICE, TOTAL_SELLING_PRICE);
    } else {
      priceRows.push(AGENCY_SELLING_PRICE, SELLING_TAXES, SUB_TOTAL_SELLING_PRICE, TOTAL_SELLING_PRICE);
    }
  }

  if (type === COST_SUMMARY) {
    switch (props.quoteType) {
      case AIR_ONLY:
        priceRows.push(BASE_COST, BASE_TAXES, SUB_TOTAL_COST, TOTAL_COST);
        break;
      case TOUR:
        priceRows.push(HOTEL_COST_LOCAL, COST_CAD_SUMMARY, COST_ADJUSTMENT_SUMMARY, FLIGHT_COST_CAD, FLIGHT_TAXES, SUB_TOTAL_COST, TOTAL_COST);
        break;
      case HOTEL_ONLY:
        priceRows.push(HOTEL_COST_LOCAL, COST_CAD_SUMMARY, COST_ADJUSTMENT_SUMMARY, SUB_TOTAL_COST, TOTAL_COST);
        break;
      default:
        priceRows.push(HOTEL_COST_LOCAL, COST_CAD_SUMMARY, COST_ADJUSTMENT_SUMMARY, TRANSFER_COST, FLIGHT_COST_CAD, FLIGHT_TAXES, SUB_TOTAL_COST, TOTAL_COST);
    }
  }

  return {
    headerName: type,
    list: priceRows
  };
};

const filterPriceList = (priceList: any, type: string) => {
  const { list } = filterRowTable(type);
  const fieldTotal = getColumnTable(type)[0]?.field;

  const mappedList = list.map((key) => ({
    ...(key === TOTAL_COST || key === TOTAL_SELLING_PRICE ? { [fieldTotal]: priceList[key] } : priceList[key]),
    rowName: key
  }));

  const adtChdRow = {
    rowName: NUMBER_OF_ADULT_AND_CHILD,
    ...priceList[NUMBER_OF_ADULT_AND_CHILD]
  };

  return [adtChdRow, ...mappedList];
};

const handleRowTotal = (rowName: string) => {
  return rowName === SUB_TOTAL_SELLING_PRICE || rowName === TOTAL_SELLING_PRICE || rowName === SUB_TOTAL_COST || rowName === TOTAL_COST;
};

const handleColumnSpan = (rowName: string) => {
  return rowName === TOTAL_SELLING_PRICE || rowName === TOTAL_COST;
};

const displayDataTable = (data: any, field: string, indexColumnChild: number) => {
  const isChildPrice = field.startsWith('childPrices');
  const value = isChildPrice ? data[field]?.[indexColumnChild] : data[field];
  if (data.rowName === NUMBER_OF_ADULT_AND_CHILD) {
    return value;
  }
  if (handleColumnSpan(data.rowName)) {
    return Number(value) ? Number(value).toFixed(2) : '';
  }
  return (Number(value) || 0).toFixed(2);
};

const formatFareBasic = (price: any) => {
  return (price.fareBasis?.split('/') ?? []).map((part: string) => part.charAt(0)).join('/');
};
</script>
<template>
  <div>
    <div v-if="getColumnTable(SELLING_PRICE_SUMMARY).length">
      <div class="mb-2">
        <div v-if="quoteType === AIR_ONLY" class="h-3rem lead-pricing flex align-items-center justify-content-between">{{ dataPricing.airClass }} - {{ formatFareBasic(dataPricing) }}</div>
        <div v-else-if="quoteType === TOUR" class="lead-pricing flex align-items-center justify-content-between">
          <div>
            ({{ formatDate(infoData.depDate, 'dd-LLL-yyyy', false, currentLanguage) }})
            {{ getTextVariant(currentLanguage, dataPricing.roomName, dataPricing.roomNameFr) }}
            ({{ dataPricing.roomCode }})
          </div>
          <div>{{ $t('proof_pricing.currency') }}: {{ dataPricing?.currency }} | ROE: {{ Number(dataPricing.roe || 0).toFixed(2) }}<br /></div>
        </div>
        <div v-else-if="quoteType === CRUISE" class="lead-pricing flex align-items-center justify-content-between">
          {{ getTextVariant(currentLanguage, dataPricing.categoryEn, dataPricing.categoryFr) }}
        </div>
        <div v-else class="lead-pricing flex align-items-center justify-content-between">
          {{ getTextVariant(currentLanguage, dataPricing.roomName, dataPricing.roomNameFr) }}
          ({{ dataPricing.roomCode }})
          <div>
            <div v-if="!isHotelCruise">{{ $t('proof_pricing.currency') }}: {{ dataPricing?.currency }} | ROE: {{ Number(dataPricing.roe || 0).toFixed(2) }}</div>
          </div>
        </div>
      </div>
      <div v-for="(price, index) in formatData(dataPricing)" :key="index">
        <div class="pricing-detail mb-3">
          <DataTable rowGroupMode="subheader" :value="filterPriceList(price.data, price.key)" class="w-full" editMode="row" data-key="rowName" showGridlines>
            <TableColumn dataType="string" class="w-30rem h-3rem relative">
              <template #header>
                <span> {{ convertOccNameTable(price.key) }} </span>
              </template>
              <template #body="{ data }">
                <div :class="[handleRowTotal(data.rowName) ? 'p-3 absolute text-left top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', handleColumnSpan(data.rowName) && 'price-header']">
                  <span>{{ convertOccNameTable(data.rowName) }}</span>
                </div>
              </template>
            </TableColumn>
            <TableColumn class="relative w-12rem" :show-filter-operator="false" :show-add-button="false" v-for="column of getColumnTable(price.key)" :key="column.field" :field="column.field">
              <template #header>
                <span>
                  {{ column.header }}
                </span>
              </template>
              <template #body="{ data, field }">
                <div :class="[handleRowTotal(data.rowName) ? 'p-3 absolute text-left top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', handleColumnSpan(data.rowName) && 'price-header']">
                  {{ displayDataTable(data, field, column.indexColumnChild) }}
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>
